// Shows exactly what goes to Foundation's staging tables and what the bridge answered.
import { useState } from "react";
import { CheckCircle2, Database, Loader2, ShieldCheck, Upload, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NOUN = { change_order: "Change Order", purchase_order: "PO" };
const TABLE = { change_order: "job_chg_phq", purchase_order: "pending_po_sync_header + pending_po_sync_item_detail" };
const show = (v) => (v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

export function FoundationStagingDialog({ kind, mode, data, accepted, busy, onClose, onDryRun, onInsertNow, testId, allowInsert = true }) {
  const rows = data.payload?.rows || data.rows_sent?.rows || [];
  const details = data.payload?.details || data.rows_sent?.details || [];
  const number = data.app_number || "";
  const fno = data.foundation_po_no || data.foundation_change_order_no;
  const insertResult = mode === "insert_now" ? data : null;
  const [confirm, setConfirm] = useState(null);
  const bridgeOk = !data.bridge || (mode === "dry_run" ? data.bridge.appends_audit_columns === false : !!data.bridge.payload_only);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4" /> {NOUN[kind]} {number} → {TABLE[kind]} {fno && fno !== number ? <span className="font-mono text-xs text-slate-500">(Foundation {fno})</span> : null}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {mode === "preview" && `Preview — rows built from live Practice reads (job phase, record_guid). Nothing was sent to the bridge.${accepted ? " A fresh accepted dry run exists for exactly these rows." : " Insert needs an accepted dry run (< 24 h) on exactly these rows."}`}
            {mode === "dry_run" && "Dry run — the bridge executed these INSERTs inside a transaction and rolled back. Nothing is left in Foundation."}
            {mode === "insert_now" && "Insert now — real INSERT of this one document."}
            {data.database ? ` Database: ${data.database}.` : ""}
          </DialogDescription>
        </DialogHeader>

        {data.blocked && (
          <div className="rounded border border-rose-200 bg-rose-50 dark:bg-rose-950/40 dark:border-rose-800 p-2 text-xs text-rose-900 dark:text-rose-100" data-testid={`${testId}-blocked`}>
            <div className="font-semibold flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Blocked before any INSERT — {data.error}</div>
            {data.detail && Object.keys(data.detail).length > 0 && <pre className="mt-1 text-[10px] whitespace-pre-wrap">{JSON.stringify(data.detail, null, 1)}</pre>}
          </div>
        )}

        {insertResult && <InsertResult st={insertResult} testId={testId} />}
        {mode === "dry_run" && !data.blocked && <DryRunResult rec={data} testId={testId} />}
        {!data.blocked && !insertResult && <BridgeNote bridge={data.bridge} mode={mode} testId={testId} />}

        {!data.blocked && !insertResult && (
          <div className="min-w-0 space-y-3">
            {data.live_match && <KeyValues title="Live match (read from Practice)" obj={data.live_match} testId={`${testId}-live-match`} />}
            {data.totals && <KeyValues title="Totals" obj={data.totals} testId={`${testId}-totals`} />}
            {kind === "purchase_order" ? (
              <div className="space-y-3">
                <KeyValues title={`pending_po_sync_header (${rows.length} row)`} obj={rows[0] || {}} testId={`${testId}-header`} mono />
                <RowsTable title={`pending_po_sync_item_detail (${details.length} rows)`} rows={details} testId={`${testId}-details`} />
              </div>
            ) : (
              <RowsTable title={`job_chg_phq (${rows.length} rows)`} rows={rows} testId={`${testId}-rows`} />
            )}
          </div>
        )}

        {confirm !== null && !insertResult && (
          <div className="rounded border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 dark:border-emerald-800 p-2 text-xs text-emerald-900 dark:text-emerald-100 min-w-0" data-testid={`${testId}-confirm`}>
            <div className="font-semibold">Real INSERT into {data.database || "Foundation"} — this cannot be undone from here.</div>
            <div className="mt-1">Type the document number <span className="font-mono">{number}</span> to confirm.</div>
            <div className="flex items-center gap-2 mt-1.5">
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={number} autoFocus
                className="font-mono text-xs px-2 py-1 rounded border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 w-44" data-testid={`${testId}-confirm-input`} />
              <button type="button" onClick={() => onInsertNow(confirm)} disabled={!!busy || confirm.trim() !== String(number).trim()}
                className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`${testId}-confirm-insert`}>
                {busy === "insert_now" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Insert {number} now
              </button>
              <button type="button" onClick={() => setConfirm(null)} className="text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600" data-testid={`${testId}-confirm-cancel`}>Cancel</button>
            </div>
          </div>
        )}

        {!data.blocked && !insertResult && <TestScript kind={kind} number={fno || number} testId={testId} />}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700 min-w-0 flex-wrap">
          {!insertResult && (
            <button type="button" onClick={onDryRun} disabled={!!busy || data.blocked} className="text-xs px-3 py-1.5 rounded border border-sky-300 text-sky-800 hover:bg-sky-50 dark:border-sky-700 dark:text-sky-200 dark:hover:bg-sky-950 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`${testId}-run-dry-run`}>
              {busy === "dry_run" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} {mode === "dry_run" ? "Run dry run again" : "Run dry run (writes nothing)"}
            </button>
          )}
          {!insertResult && confirm === null && allowInsert && (
            <button type="button" onClick={() => setConfirm("")} disabled={!!busy || !accepted || data.blocked || !bridgeOk}
              title={!bridgeOk ? "Blocked until the bridge is updated to 2.6.1 (payload columns only)" : accepted ? "Real INSERT of this document (asks you to type the number)" : "Needs an accepted dry run (< 24 h) on exactly these rows first"}
              className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`${testId}-insert-now`}>
              <Upload className="w-3 h-3" /> Insert now…
            </button>
          )}
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid={`${testId}-close`}>Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BridgeNote({ bridge, mode, testId }) {
  if (!bridge) return null;
  const ok = mode === "dry_run" ? bridge.appends_audit_columns === false : !!bridge.payload_only;
  const v = bridge.version || "unreachable";
  return (
    <div className={`rounded border p-2 text-[11px] ${ok ? "border-emerald-200 bg-emerald-50/60 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-100" : "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:border-amber-700 dark:text-amber-100"}`} data-testid={`${testId}-bridge-note`}>
      {ok
        ? <>Bridge {v}: payload columns only — Foundation fills <span className="font-mono">row_created_*</span>, <span className="font-mono">row_modified_*</span>, <span className="font-mono">record_guid</span> from its own defaults (Tom, 10/21).</>
        : <>Bridge {v} still appends <span className="font-mono">SUSER_SNAME()/GETDATE()/NEWID()</span> audit columns — Tom says let Foundation auto-populate them. Dry runs work; <b>Insert now is blocked until IT updates the bridge to 2.6.1</b> (Settings › Integrations › Foundation › Bridge Ops › Update bridge).</>}
    </div>
  );
}

const PO_SCRIPT = (n) => [
  `Foundation (Practice) → PO/Subcontract → PO/Sub and Change Order Sync → Report tab.`,
  `Tick Import on ${n} → Import. The PO form opens in ADD mode with the header filled, an EMPTY line grid and "Processing…" — that is the expected pre-OK state.`,
  `Press OK on the ADD form → the item lines should load. Open the Item Detail tab and confirm every line (job W222 · phase 1 · 20-100000 · class 2 / 4).`,
  `Press OK again → "imported" confirmation. Do NOT Delete the row.`,
  `Tell us the exact text of any message instead (Report sync error) — then we read back po_created and PO_Sub_H here.`,
];
const CO_SCRIPT = (n) => [
  `Foundation (Practice) → Job Costing → J/C Change Orders Sync → Criteria as defaulted (then also with the booking month).`,
  `Report tab should list change order ${n} for job W222 (one row per CO).`,
  `Tick Import → Import → check the cost classes / amounts → OK. Do NOT Delete the row.`,
  `Paste any message verbatim into Report sync error; we read back job_chg_phq.imported here.`,
];
function TestScript({ kind, number, testId }) {
  const steps = (kind === "purchase_order" ? PO_SCRIPT : CO_SCRIPT)(number);
  return (
    <details className="text-[11px] text-slate-600 dark:text-slate-300" data-testid={`${testId}-test-script`}>
      <summary className="cursor-pointer font-semibold">Accounting's script after the insert ({steps.length} steps)</summary>
      <ol className="list-decimal ml-4 mt-1 space-y-0.5">{steps.map((t, i) => <li key={i}>{t}</li>)}</ol>
    </details>
  );
}

function DryRunResult({ rec, testId }) {
  const r = rec.response || {};
  const ok = rec.accepted;
  const tone = ok ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-100"
    : "border-rose-200 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:border-rose-800 dark:text-rose-100";
  return (
    <div className={`rounded border p-2 text-xs ${tone}`} data-testid={`${testId}-result`}>
      <div className="font-semibold flex items-center gap-1" data-testid={`${testId}-result-headline`}>
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
        {ok && `Accepted by SQL Server — rolled back (would insert ${Object.entries(r.would_insert || {}).map(([t, n]) => `${n} × ${t}`).join(", ") || "rows"}), 0 rows left`}
        {!ok && rec.nothing_sent && `Could not reach Foundation — nothing was sent (${rec.bridge_attempts} attempt(s))`}
        {!ok && !rec.nothing_sent && rec.duplicate && "Already staged in Foundation — duplicate key, nothing written"}
        {!ok && !rec.nothing_sent && !rec.duplicate && "Rejected by SQL Server — nothing written (transaction rolled back)"}
      </div>
      <div className="mt-1 text-[11px] opacity-80">
        bridge {r.version || "—"} · HTTP {rec.status_code} · db {r.database || rec.database || "—"} · login {r.login || "—"} · {rec.at?.slice(0, 19).replace("T", " ")} by {rec.by || "—"}
      </div>
      {rec.error && <pre className="mt-1 text-[10px] whitespace-pre-wrap font-mono" data-testid={`${testId}-result-error`}>{rec.error}{r.sql ? `\n\n${r.sql}` : ""}</pre>}
      {Array.isArray(r.statements) && r.statements.length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-[11px]">SQL executed ({r.statements.length} statement(s))</summary>
          <pre className="text-[10px] whitespace-pre-wrap font-mono mt-1">{r.statements.map(s => `-- ${s.table}\n${s.sql}\n${JSON.stringify(s.values)}`).join("\n\n")}</pre>
        </details>
      )}
    </div>
  );
}

function InsertResult({ st, testId }) {
  const ok = st.status === "pushed" || st.status === "verified";
  return (
    <div className={`rounded border p-2 text-xs ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-rose-200 bg-rose-50 text-rose-900"}`} data-testid={`${testId}-insert-result`}>
      <div className="font-semibold flex items-center gap-1">{ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {st.label || st.status} — {st.message}</div>
      {st.response?.inserted && <div className="mt-1 text-[11px]">inserted: {Object.entries(st.response.inserted).map(([t, n]) => `${n} × ${t}`).join(", ")} · db {st.response.database} · login {st.response.login}</div>}
    </div>
  );
}

function KeyValues({ title, obj, testId, mono }) {
  const entries = Object.entries(obj || {});
  if (!entries.length) return null;
  return (
    <div data-testid={testId} className="min-w-0">
      <div className="text-[11px] font-semibold text-slate-700 dark:text-slate-200 mb-1">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0.5 text-[11px]">
        {entries.map(([k, v]) => (
          <div key={k} className="flex gap-1 min-w-0"><span className="text-slate-500 shrink-0">{k}:</span><span className={`break-all ${mono || /guid|hash/i.test(k) ? "font-mono" : ""}`}>{show(v)}</span></div>
        ))}
      </div>
    </div>
  );
}

function RowsTable({ title, rows, testId }) {
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach(k => s.add(k)); return s; }, new Set()));
  return (
    <div data-testid={testId} className="min-w-0 max-w-full">
      <div className="text-[11px] font-semibold text-slate-700 dark:text-slate-200 mb-1">{title}</div>
      {!rows.length && <div className="text-[11px] text-slate-400">No rows.</div>}
      {rows.length > 0 && (
        <div className="overflow-x-auto max-w-full rounded border border-slate-200 dark:border-slate-700">
          <table className="text-[10px] font-mono whitespace-nowrap">
            <thead className="bg-slate-50 dark:bg-slate-800"><tr>{cols.map(c => <th key={c} className="px-1.5 py-0.5 text-left font-semibold">{c}</th>)}</tr></thead>
            <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-100 dark:border-slate-700">{cols.map(c => <td key={c} className="px-1.5 py-0.5">{show(r[c]).replace(/ /g, "\u00a0")}</td>)}</tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
